import React, { useContext, useState, createContext, useMemo } from "react";
import { getUserOrgs } from "@/services/users";

interface IOrganizationContext {
  organizationId: string | null;
  userOrganizations: Array<string>;
  setOrganization: (value: string | null) => void;
  updateUserOrganizations: (user: string) => void;
  updateOrganizationName: (organizationId: string, newName: string) => void;
}

const OrganizationContext = createContext<IOrganizationContext | null>(null);

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error(
      "useOrganization must be used within the OrganizationProvider"
    );
  }
  return context;
};

export const OrganizationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [userOrganizations, setUserOrganizations] = useState<Array<any>>([]);
  const setOrganization = (value: string | null) => setOrganizationId(value);

  const updateUserOrganizations = async (user: any) => {
    if (!user) {
      setUserOrganizations([]);
      return;
    }
    const orgs = await getUserOrgs();
    setUserOrganizations(orgs);
  };

  const updateOrganizationName = (organizationId: string, newName: string) => {
    setUserOrganizations((prevOrgs) =>
      prevOrgs.map((org) =>
        org.organization_id === organizationId ? { ...org, name: newName } : org
      )
    );
  };

  const value = useMemo(
    () => ({
      organizationId,
      userOrganizations,
      setOrganization,
      updateUserOrganizations,
      updateOrganizationName,
    }),
    [organizationId, userOrganizations]
  );

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};
